<template>
	<v-autocomplete
		ref="v_autocomplete"
		:append-icon="appendIcon"
		:auto-grow="autoGrow"
		:autofocus="autofocus"
		:background-color="backgroundColor"
		:chips="chips"
		:clearable="clearable"
		:debounce-search="debounceSearch"
		:deletable-chips="deletableChips"
		:dense="dense"
		:disabled="disabled"
		:error-messages="errorMessages"
		:error="error"
		:filter="filter"
		:hide-details="hideDetails"
		:hide-no-data="hideNoData"
		:hide-selected="hideSelected"
		:hint="hint"
		:item-text="itemText"
		:items="items"
		:label="label"
		:loading="loading"
		:menu-props="menuProps"
		:multiple="multiple"
		:outlined="outlined"
		:persistent-hint="persistentHint"
		:placeholder="placeholder"
		:prepend-inner-icon="prependInnerIcon"
		:return-object="returnObject"
		:rules="rules"
		:search-input="searchInput"
		:small-chips="smallChips"
		:solo="solo"
		:value="value"
		v-bind="forwardedAttrs"
		v-on="other_listeners"
		@click.native="$emit('click', $event)"
		@input="$emit('input', $event)"
		@change="$emit('change', $event)"
		@keydown="$emit('keydown', $event)"
		@blur="$emit('blur', $event)"
		@click:append="$emit('click:append', $event)"
	>
		<template v-slot:label>
			<slot name="label"></slot>
		</template>
		<template v-if="$scopedSlots.item" v-slot:item="slotProps">
			<slot name="item" v-bind="slotProps"></slot>
		</template>
	</v-autocomplete>
</template>

<script>
export default {
	name: 'KAutocomplete',
	inheritAttrs: false,
	props: {
		appendIcon: String,
		autoGrow: Boolean,
		autofocus: Boolean,
		backgroundColor: String,
		value: null,
		clearable: Boolean,
		chips: Boolean,
		debounceSearch: [Number, String],
		deletableChips: Boolean,
		dense: Boolean,
		disabled: Boolean,
		error: Boolean,
		errorMessages: [String, Array],
		filter: Function,
		hideDetails: {
			type: [Boolean, String],
			default: 'auto'
		},
		hideNoData: Boolean,
		hideSelected: Boolean,
		hint: String,
		itemText: [String, Array, Function],
		items: Array,
		label: String,
		loading: [Boolean, String],
		menuProps: [String, Array, Object],
		multiple: Boolean,
		outlined: Boolean,
		persistentHint: Boolean,
		placeholder: String,
		prependInnerIcon: String,
		returnObject: Boolean,
		rules: [Array, Function],
		searchInput: String,
		smallChips: Boolean,
		solo: Boolean,
		accessibleLabel: String,
	},
	computed: {
		forwardedAttrs() {
			const { 'accessible-label': accessible_label, ...rest } = this.$attrs
			if (this.resolvedAriaLabel && !rest['aria-label'] && !rest['aria-labelledby']) {
				rest['aria-label'] = this.resolvedAriaLabel
			}
			return rest
		},
		other_listeners() {
			const { click, input, change, keydown, blur, 'click:append': clickAppend, ...rest } = this.$listeners;
			return rest;
		},
		resolvedAriaLabel() {
			return this.accessibleLabel || this.$attrs['aria-label']
		},
		/** 
		 *  This is for places in the apps where we expose the menu state of the autocomplete.
		 *  We need to sync this with the internal state of the v-autocomplete to ensure that it works
		 *  correctly when the menu is opened/closed from either the parent or the child component.
		 */
		isMenuActive: {
			get() {
				return this.$refs.v_autocomplete.isMenuActive;
			},
			set(value) {
				this.$refs.v_autocomplete.isMenuActive = value;
			}
		}
	},
	mounted() {
		this.sync_hidden_legend_accessibility()
		this.sync_input_accessibility()
		this.sync_clear_icon_accessibility()
	},
	updated() {
		this.sync_hidden_legend_accessibility()
		this.sync_input_accessibility()
		this.sync_clear_icon_accessibility()
	},
	methods: {
		/** We expose the blur methods to allow parent components to control focus state. */ 
		blur() {
			this.$refs.v_autocomplete.blur();
		},
		focus() {
			this.$refs.v_autocomplete.focus();
		},
		sync_input_accessibility() {
			const root = this.$refs.v_autocomplete?.$el
			if (!root || !this.resolvedAriaLabel) return

			const input = root.querySelector('input, textarea')
			if (!input) return

			input.setAttribute('aria-label', this.resolvedAriaLabel)
		},
		sync_hidden_legend_accessibility() {
			const root = this.$refs.v_autocomplete?.$el
			if (!root) return

			const hidden_legend_span = root.querySelector('fieldset[aria-hidden="true"] legend span')
			if (!hidden_legend_span) return

			hidden_legend_span.setAttribute('aria-hidden', 'true')
			hidden_legend_span.style.opacity = '0'
			hidden_legend_span.style.color = 'transparent'
			hidden_legend_span.style.backgroundColor = 'transparent'
		},
		sync_clear_icon_accessibility() {
			const root = this.$refs.v_autocomplete?.$el
			if (!root || !this.clearable) return

			const clear_control = root.querySelector('.v-input__icon--clear [role="button"], .v-input__icon--clear button, .v-input__icon--clear')
			if (!clear_control) return

			clear_control.setAttribute('tabindex', '0')
			clear_control.setAttribute('aria-label', `Clear ${this.resolvedAriaLabel || this.label || 'selection'}`)

			if (clear_control.dataset.kKeyboardClearBound === 'true') return

			clear_control.addEventListener('keydown', event => {
				if (event.key !== 'Enter' && event.key !== ' ') return

				event.preventDefault()
				event.stopPropagation()

				const cleared_value = this.multiple ? [] : null
				this.$emit('input', cleared_value)
				this.$emit('change', cleared_value)

				this.$nextTick(() => {
					const input = root.querySelector('input, textarea')
					if (input) input.focus()
				})
			})

			clear_control.dataset.kKeyboardClearBound = 'true'
		},
	}
};
</script>
